import type { QueryClient } from "@tanstack/react-query";
import { QUERY_BASE_KEY } from "@constants/query-key.ts";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { Version } from "@/hooks/roster-craete/useRosterVersions";
import type { CurScheduleInfo } from "@/store/scheduleStore";
import { SelectVersionHandler } from "@utils/rosterCreate-util.ts";

export async function invalidateRosterCreateCaches(
  queryClient: QueryClient,
  params: {
    year: number | undefined;
    month: number | undefined;
    selectedGroupId: string | null;
  }
): Promise<void> {
  const { year, month, selectedGroupId } = params;
  if (year === undefined || month === undefined) return;

  await queryClient.invalidateQueries({
    queryKey: ["VERSIONS", year, month],
  });
  await queryClient.invalidateQueries({
    queryKey: ["ROSTER_ISSUED_ROSTER", year, month],
  });
  await queryClient.invalidateQueries({
    queryKey: [QUERY_BASE_KEY.ISSUED, selectedGroupId],
  });
  await queryClient.invalidateQueries({ queryKey: ["alarms"] });
  await queryClient.invalidateQueries({ queryKey: ["ROSTER_SCHEDULE"] });
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.wanted.status(year, month, selectedGroupId ?? ""),
  });
  await queryClient.invalidateQueries({ queryKey: ["manpower"] });
  await queryClient.invalidateQueries({
    queryKey: ["ROSTER_PREV_TAIL", year, month],
  });
}

export async function refetchVersionsAndSelectLatestSchedule(
  queryClient: QueryClient,
  params: {
    year: number;
    month: number;
    isAdmin: boolean;
    selectedGroupId: string | null;
  },
  setCurScheduleInfo: (info: CurScheduleInfo | null) => void
): Promise<void> {
  const versionGroupKey = params.isAdmin ? params.selectedGroupId : null;

  await queryClient.refetchQueries({
    queryKey: ["VERSIONS", params.year, params.month],
  });

  const list = queryClient.getQueryData<Version[]>([
    "VERSIONS",
    params.year,
    params.month,
    versionGroupKey,
  ]);
  const selected = list?.length ? SelectVersionHandler(list, "current") : null;

  if (selected) {
    setCurScheduleInfo(selected);
  }
}

export async function invalidateRosterCachesAndSelectLatestSchedule(
  queryClient: QueryClient,
  params: {
    year: number;
    month: number;
    selectedGroupId: string | null;
    isAdmin: boolean;
  },
  setCurScheduleInfo: (info: CurScheduleInfo | null) => void
): Promise<void> {
  await invalidateRosterCreateCaches(queryClient, {
    year: params.year,
    month: params.month,
    selectedGroupId: params.selectedGroupId,
  });
  await refetchVersionsAndSelectLatestSchedule(
    queryClient,
    {
      year: params.year,
      month: params.month,
      isAdmin: params.isAdmin,
      selectedGroupId: params.selectedGroupId,
    },
    setCurScheduleInfo
  );
}
